import { useEffect, type RefObject } from 'react';

const GUARD_POLL_MS = 400;

function isElementVisible(element: HTMLElement | null): boolean {
  if (!element || !element.isConnected) {
    return false;
  }

  const style = window.getComputedStyle(element);

  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  const opacity = Number.parseFloat(style.opacity);
  return !Number.isNaN(opacity) && opacity > 0.01;
}

function isGuardLayerIntact(guardHost: HTMLElement | null, expectsDialog: boolean): boolean {
  if (!guardHost || !isElementVisible(guardHost)) {
    return false;
  }

  const overlay = guardHost.querySelector<HTMLElement>('[data-blocking-guard-overlay]');
  if (!isElementVisible(overlay)) {
    return false;
  }

  if (!expectsDialog) {
    return true;
  }

  const dialog = guardHost.querySelector<HTMLElement>('[data-blocking-guard-dialog]');
  return isElementVisible(dialog);
}

function enforceBlockedContent(blockedContent: HTMLElement | null): void {
  if (!blockedContent || !blockedContent.isConnected) {
    return;
  }

  if (!blockedContent.hasAttribute('inert')) {
    blockedContent.setAttribute('inert', '');
  }

  if (blockedContent.getAttribute('aria-hidden') !== 'true') {
    blockedContent.setAttribute('aria-hidden', 'true');
  }
}

interface UseBlockingGuardIntegrityOptions {
  active: boolean;
  expectsDialog: boolean;
  guardHostRef: RefObject<HTMLElement | null>;
  blockedContentRef: RefObject<HTMLElement | null>;
  onRestore: () => void;
}

export function useBlockingGuardIntegrity({
  active,
  expectsDialog,
  guardHostRef,
  blockedContentRef,
  onRestore,
}: UseBlockingGuardIntegrityOptions) {
  useEffect(() => {
    if (!active) {
      return;
    }

    let restoreScheduled = false;

    const scheduleRestore = () => {
      if (restoreScheduled) {
        return;
      }

      restoreScheduled = true;
      window.requestAnimationFrame(() => {
        restoreScheduled = false;
        onRestore();
      });
    };

    const verifyIntegrity = () => {
      enforceBlockedContent(blockedContentRef.current);

      if (!isGuardLayerIntact(guardHostRef.current, expectsDialog)) {
        scheduleRestore();
      }
    };

    verifyIntegrity();

    const observer = new MutationObserver(() => {
      verifyIntegrity();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'inert', 'aria-hidden'],
    });

    const intervalId = window.setInterval(verifyIntegrity, GUARD_POLL_MS);

    return () => {
      observer.disconnect();
      window.clearInterval(intervalId);
    };
  }, [active, blockedContentRef, expectsDialog, guardHostRef, onRestore]);
}
